import { useEffect, useState } from "react";
import type { BookingProposal } from "../api/ai.api";
import {
  LOCAL_BOOKINGS_CHANGED_EVENT,
  LOCAL_BOOKINGS_STORAGE_KEY,
  readLocalBookings,
} from "../../account/lib/local-bookings";

/**
 * Has the guest sent the request the chat proposed? (chat kit C11)
 *
 * "Request sent" may only be said on proof that a booking exists, and the
 * proof is the browser's own list of bookings (`mj-local-bookings`): the
 * booking sheet adds a row the moment the API accepts a booking
 * (`onBookingCreated`), the thank-you page adds the same row when it loads, and
 * since 2026-10-12 the chat's own booking card (ChatBookingCard) adds it when
 * the checkout accepts a request sent from the chat. All run only for a booking
 * that exists.
 *
 * A row in that list is not enough on its own: the guest may have booked the
 * same place last month. It counts as THIS proposal's request only when
 *
 *   1. the guest pressed this proposal's button in the chat (recorded here,
 *      synchronously, before the page changes), and
 *   2. a booking for the same place on the same day was saved after that press,
 *   3. and it has not been cancelled.
 *
 * A date changed in the form therefore reads as "not sent" here. That is the
 * safe side: the card goes on saying "Awaiting your confirmation" rather than
 * claiming a request we cannot match.
 */

const OPENED_KEY = "mj_ai_proposal_opened_v1";
/** Same-tab signal, so a second chat surface on the page re-checks at once. */
export const PROPOSAL_OPENED_EVENT = "mj-ai-proposal-opened";
/** Presses older than this are forgotten; the match would be stale anyway. */
const OPENED_TTL_MS = 14 * 24 * 60 * 60 * 1000;
/** Clock wobble between the press and the save, both on this device. */
const SKEW_MS = 5_000;

export interface RequestSentInfo {
  reference: string;
  date: string;
  time?: string;
  guests: number;
  /** When the booking was saved on this device. */
  at: number;
}

type OpenedMap = Record<string, { slug: string; date: string; at: number }>;

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function readOpened(): OpenedMap {
  if (!hasStorage()) return {};
  try {
    const raw = window.localStorage.getItem(OPENED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === "object" ? (parsed as OpenedMap) : {};
  } catch {
    return {};
  }
}

/**
 * Called from the button's click, before the browser leaves the page. Written
 * straight to storage rather than through React state, which would not be
 * flushed before a full navigation.
 */
export function markProposalOpened(messageId: string, proposal: Pick<BookingProposal, "slug" | "date">): void {
  if (!hasStorage()) return;
  const now = Date.now();
  const map = readOpened();
  for (const [id, v] of Object.entries(map)) {
    if (now - v.at > OPENED_TTL_MS) delete map[id];
  }
  // Keep the first press: a second press must not move the window forward
  // past a booking the first one already produced.
  if (!map[messageId]) map[messageId] = { slug: proposal.slug, date: proposal.date.slice(0, 10), at: now };
  try {
    window.localStorage.setItem(OPENED_KEY, JSON.stringify(map));
    window.dispatchEvent(new CustomEvent(PROPOSAL_OPENED_EVENT));
  } catch {
    // Private mode: the card simply never turns into "Request sent".
  }
}

/** The booking that proves this proposal was sent, or null. */
export function findSentRequest(
  messageId: string,
  proposal: Pick<BookingProposal, "slug" | "date">,
): RequestSentInfo | null {
  const opened = readOpened()[messageId];
  if (!opened || opened.slug !== proposal.slug) return null;
  const day = proposal.date.slice(0, 10);
  const hit = readLocalBookings().find(
    (b) =>
      b.slug === proposal.slug &&
      b.date.slice(0, 10) === day &&
      b.status !== "cancelled" &&
      b.savedAt >= opened.at - SKEW_MS,
  );
  if (!hit) return null;
  return {
    reference: hit.reference,
    date: hit.date.slice(0, 10),
    ...(hit.time ? { time: hit.time.slice(0, 5) } : {}),
    guests: hit.guests,
    at: hit.savedAt,
  };
}

interface ProposalLike {
  kind: string;
  id: string;
  bookingProposal?: BookingProposal;
  requestSent?: RequestSentInfo;
}

/**
 * Stamp `requestSent` on every proposal turn that now has its proof. Returns
 * the same array when nothing changed, so callers can skip a write.
 */
export function reconcileRequestSent<M extends ProposalLike>(messages: M[]): M[] {
  let changed = false;
  const next = messages.map((m) => {
    if (m.kind !== "assistant" || !m.bookingProposal || m.requestSent) return m;
    const sent = findSentRequest(m.id, m.bookingProposal);
    if (!sent) return m;
    changed = true;
    return { ...m, requestSent: sent };
  });
  return changed ? next : messages;
}

/** Re-runs `check` whenever the proof could have changed. */
export function subscribeRequestSignals(check: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === LOCAL_BOOKINGS_STORAGE_KEY || e.key === OPENED_KEY) check();
  };
  // Coming back to the tab is the usual way back from the form.
  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(LOCAL_BOOKINGS_CHANGED_EVENT, check);
  window.addEventListener(PROPOSAL_OPENED_EVENT, check);
  window.addEventListener("focus", check);
  window.addEventListener("pageshow", check);
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(LOCAL_BOOKINGS_CHANGED_EVENT, check);
    window.removeEventListener(PROPOSAL_OPENED_EVENT, check);
    window.removeEventListener("focus", check);
    window.removeEventListener("pageshow", check);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/**
 * The live answer for one proposal turn: what the stored conversation already
 * says, or what the signals say now. Read after mount only, so the server and
 * the first client render agree.
 */
export function useRequestSent(
  messageId: string,
  proposal: BookingProposal | undefined,
  stored: RequestSentInfo | undefined,
): RequestSentInfo | null {
  const [live, setLive] = useState<RequestSentInfo | null>(null);
  const slug = proposal?.slug;
  const date = proposal?.date;
  useEffect(() => {
    if (stored || !slug || !date) return;
    const check = () => setLive(findSentRequest(messageId, { slug, date }));
    check();
    return subscribeRequestSignals(check);
  }, [messageId, slug, date, stored]);
  return stored ?? live;
}
